import {makeAutoObservable, observable, runInAction} from 'mobx'
import {rightPanelStore} from '@components/right-panel/right-panel'
import {SearchStore} from '@components/right-panel/search/search-store'
import {createUseStore} from '@containers/hooks'

const defaultPopupWidth = 536
const minPopupWidth = 420
const popupMargin = 4
const defaultPopupHeight = 545
const minPopupHeight = 360

interface State {
  opened: boolean
  popupReferenceEl?: HTMLElement
  popupX: number
  popupY: number
  popupWidth: number
  popupHeight: number
}

export class SearchPopupStore {
  state: State = {
    opened: false,
    popupX: 0,
    popupY: 0,
    popupWidth: defaultPopupWidth,
    popupHeight: defaultPopupHeight
  }

  searchStore: SearchStore
  resizeHandler: number | undefined

  constructor(searchStore: SearchStore) {
    makeAutoObservable(
      this,
      {
        state: observable.shallow,
        searchStore: observable.ref,
        resizeHandler: observable.ref
      },
      {autoBind: true}
    )

    this.searchStore = searchStore
  }

  tryClosePopup(): boolean {
    if (!this.state.opened) {
      return false
    }

    this.state.opened = false
    this.searchStore.stopSearch()
    return true
  }

  tryHidePopup(): boolean {
    if (!this.state.opened) {
      return false
    }

    this.state.opened = false
    return true
  }

  tryOpenPopup(): boolean {
    if (this.state.opened) {
      this.openPopup()
      return true
    }

    if (this.isAnotherPopupOpened()) {
      return false
    }

    this.tryCloseDropdown()
    this.tryCloseRightPanelSearch()
    setTimeout(this.openPopup)
    return true
  }

  onFakeInputFocus(): void {
    if (!this.state.opened) {
      this.tryCloseDropdown()
      setTimeout(this.openPopup)
    } else {
      this.openPopup()
    }
  }

  private openPopup(): void {
    this.state.opened = true
    this.tryRepositionPopup()
    this.searchStore.startSearch()
  }

  setPopupReferenceEl(el: HTMLElement | null | undefined): void {
    if (el) {
      this.state.popupReferenceEl = el
    }
  }

  private isAnotherPopupOpened(): boolean {
    // Check MoveToBody contents for visibility
    return this.isAnyVisible('body > [data-origin] > *:first-child')
  }

  private isDropdownOpened(): boolean {
    // Check Tippy dropdowns for visibility
    return this.isAnyVisible('body > [data-tippy-root]')
  }

  private isAnyVisible(selector: string): boolean {
    const els = document.querySelectorAll(selector)
    for (const el of Array.from(els)) {
      if (el instanceof HTMLElement && el.offsetParent) {
        return true
      }
    }
    return false
  }

  private tryCloseDropdown(): void {
    if (this.isDropdownOpened()) {
      document.dispatchEvent(new KeyboardEvent('keydown', {key: 'Escape'}))
    }
  }

  private tryCloseRightPanelSearch(): void {
    rightPanelStore.closeTaskChatListOpenedBlock('search')()
  }

  tryRepositionPopup(): void {
    if (!this.resizeHandler) {
      this.resizeHandler = requestAnimationFrame(() => {
        this.resizeHandler = undefined
        if (!this.state.popupReferenceEl) {
          return
        }

        const rect = this.state.popupReferenceEl.getBoundingClientRect()
        // Where a reference input is minus a padding
        const baseX = rect.x - 8
        // Where right panel ends plus some margin
        const rightThreshold =
          window.innerWidth -
          (rightPanelStore.state.chatPanel.opened
            ? rightPanelStore.state.chatPanel.width
            : 0) -
          popupMargin

        runInAction(() => {
          if (baseX + defaultPopupWidth < rightThreshold) {
            this.state.popupX = baseX
            this.state.popupWidth = defaultPopupWidth
          } else if (popupMargin + defaultPopupWidth < rightThreshold) {
            this.state.popupX = rightThreshold - defaultPopupWidth
            this.state.popupWidth = defaultPopupWidth
          } else {
            this.state.popupX = popupMargin
            this.state.popupWidth = Math.max(
              rightThreshold - popupMargin,
              minPopupWidth
            )
          }

          this.state.popupY = popupMargin
          if (window.innerHeight > defaultPopupHeight + 2 * popupMargin) {
            this.state.popupHeight = defaultPopupHeight
          } else if (window.innerHeight > minPopupHeight + 2 * popupMargin) {
            this.state.popupHeight = window.innerHeight - 2 * popupMargin
          } else {
            this.state.popupHeight = minPopupHeight
          }
        })
      })
    }
  }
}

export const [SearchPopupContext, useSearchPopupStore] =
  createUseStore(SearchPopupStore)
